/* eslint-disable no-unused-expressions,no-plusplus,prefer-destructuring,object-curly-newline */
/* global G */
import { asyncPipeSpread, bulk } from 'lib/util'
import { hide, show } from 'lib/sequence/component/state/hidden'
import { reset, set } from 'lib/sequence/component/state/value'
import sequenceComponentState from 'lib/sequence/component/state'
import find from 'lib/sequence/component/children/find'
import asObject from 'lib/sequence/component/children/asObject'
import { SEARCH } from 'app/_shared/events/setStepTab'
import initSerialImage from 'app/_shared/action/partial/initSerialImage'
import initSerialField from 'app/_shared/action/partial/initSerialField'
import { settings } from '@app/_shared/session'
import finish from 'app/_shared/action/partial/finish'

const {
  unset: clearError,
} = sequenceComponentState('error')

const {
  unset: clearHelperText,
} = sequenceComponentState('helperText')

const {
  set: setAttachment,
  unset: clearAttachment,
} = sequenceComponentState('attachment')

const getAvatar = model => model[G.CHILDREN].product[G.CACHE]?.refs?.avatar?.[0]
const clear = component => clearError(component) && clearHelperText(component)
const bulkHide = bulk(hide)

/**
 * Hides the `addToFavorites` checkbox.
 *
 * @param {Gaia.AppModule.Spec} module  the current module
 * @return {function(*, ...[*]): Promise<*[]>}
 */
const initAddToFavorites = module => async (components, ...args) => {
  const { addToFavorites } = components

  hide(addToFavorites)

  return [components, ...args]
}

/**
 * Sets step header values and flags according to the model data.
 *
 * @param {Gaia.AppModule.Spec} module  the current module
 * @return {function(*, ...[*]): Promise<*[]>}
 */
const setStepState = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { itemData } = model[G.CHILDREN]
  const stepState = components.component[G.STATE]

  // if there is a selected product
  if (itemData[G.CACHE]) {
    const { name, serial, serialDistinction } = itemData[G.CACHE].value
    // set the step as complete
    stepState.completed = true
    // fill step's title and subtitle with model data
    stepState.title = name
    stepState.subtitle = serialDistinction ?? serial
  } else {
    // set step as incomplete and unconfirmed
    stepState.completed = false
    // clear step's title and subtitle
    stepState.title = ''
    stepState.subtitle = ''
  }

  return [components, ...args]
}

/**
 * Sets the device image in the confirmation step.
 *
 * @param {Gaia.AppModule.Spec} module  the current module
 * @return {function(*, ...[*]): Promise<*[]>}
 */
const setDeviceImage = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { itemData } = model[G.CHILDREN]
  const { deviceImage } = components

  itemData[G.CACHE]
    ? setAttachment(deviceImage, getAvatar(model))
    : clearAttachment(deviceImage)

  return [components, ...args]
}


/**
 * Sets the information displayed in the 'confirm' step from the model data.
 *
 * @param {Gaia.AppModule.Spec} module  the current module
 * @return {function(*, ...[*]): Promise<*[]>}
 */
const setConfirmationInfo = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { itemData } = model[G.CHILDREN]

  const { productName, numberCommission: numberCommissionText, deviceInfo } = components
  const { serial: serialText } = asObject(deviceInfo[G.CHILDREN])

  hide(productName, settings.suppressProductDevices)

  if (itemData[G.CACHE]) {
    const { serial, name, numberCommission} = itemData[G.CACHE]?.value || {}

    set(serialText, serial)
    set(productName, name)

    numberCommission
      ? set(numberCommissionText, numberCommission)
        && show(numberCommissionText)
      : hide(numberCommissionText)
  } else {
    reset(serialText)
    reset(productName)
    reset(numberCommissionText)
  }

  return [components, ...args]
}

/**
 * Clears the field and buttons of the identification step if it isn't the current one.
 *
 * @param {Object} components component finder proxy
 * @param {...*} args         action call arguments
 * @return {Promise<*[]>}
 */
const clearIdentificationStep = async (components, ...args) => {
  const stepState = components.component[G.STATE]

  const { existingHelp, btnMore, form } = components
  const { serial: serialField } = asObject(form[G.CHILDREN])

  if (stepState.activeTab !== SEARCH) {
    bulkHide(existingHelp, btnMore)
    clear(serialField)
  }

  return [components, ...args]
}

/**
 * Device identification step action.
 *
 * Handles the display and status of the step's elements.
 *
 * Displays 3 different sub-steps:
 * - If the current user can see any service item, a list of selectable service items will be shown.
 * - If the user cannot see any service item or clicks on "Select another device", a search section
 *    will be shown, allowing the user to find a service item, an equipment or a product.
 * - If the search doesn't return any item, the serial number is kept and the "Cannot identify
 *    device?" button is displayed. Clicking on it brings to a list of selectable products.
 *
 * The selected/found elements will be used to compose the request object.
 *
 * @param {Gaia.AppModule.Spec} module  the current module
 * @returns {function(*=): function(...[*]): Promise<*[]>}
 */
export default module => component => async (...args) => asyncPipeSpread(
  initSerialImage(module),
  initSerialField(module),
  initAddToFavorites(module),
  setStepState(module),
  setDeviceImage(module),
  setConfirmationInfo(module),
  clearIdentificationStep,
  finish,
)(find(component), ...args)
